import React, { PropsWithChildren, useContext } from 'react'
import clsx from 'clsx'
import { Trans } from 'react-i18next'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { injectDataAttributes } from '@utils/dataAttributes'
import { SVGIcon, SVGIconProps } from '../SVGIcon/SVGIcon'
import { ButtonLink } from '../ButtonLink/ButtonLink'
import { AlertContext } from './context'
import styles from './styles/Alert.module.scss'

type AlertProps = {
  severity?: 'success' | 'info' | 'warning' | 'error'
  className?: string
  contentClassName?: string
  smallText?: boolean
  borderless?: boolean
  iconProps?: SVGIconProps
} & PropsWithChildren

export const Alert: React.FC<AlertProps> = props => {
  const { severity = 'info', className, contentClassName, smallText, borderless, iconProps, children, ...rest } = props

  const svgIconProps = {
    library: 'base',
    name: severity,
    className: styles.alertIcon,
    ...iconProps,
  } as SVGIconProps

  return (
    <AlertContext.Provider value={{ smallText }}>
      <div
        className={clsx(
          styles.container,
          styles[severity],
          smallText && styles.smallText,
          borderless && styles.borderless,
          className
        )}
        {...rest}
      >
        <SVGIcon {...svgIconProps} />
        <div className={clsx(styles.contentContainer, contentClassName)} data-name="content-container">
          {children}
        </div>
      </div>
    </AlertContext.Provider>
  )
}

type AlertComponentProps = PropsWithChildren & {
  className?: string
}

export const AlertHeader: React.FC<AlertComponentProps> = ({ className, children }) => {
  const { smallText } = useContext(AlertContext)
  return (
    <div className={clsx(styles.header, smallText && styles.smallText, className)} data-name="header">
      {children}
    </div>
  )
}

export const AlertContent: React.FC<AlertComponentProps> = ({ className, children }) => {
  const { smallText } = useContext(AlertContext)
  return (
    <div className={clsx(styles.content, smallText && styles.smallText, className)} data-name="content">
      {children}
    </div>
  )
}

type AlertTextProps = {
  text?: string | string[]
}

export const AlertText: React.FC<AlertTextProps> = ({ text = [] }) => {
  const textLines = typeof text === 'string' ? [text] : text
  return (
    <>
      {textLines.map((line, index) => (
        <span
          key={line ? line.substring(0, 20) : `line_${index}`}
          className={clsx(styles.message)}
          dangerouslySetInnerHTML={{ __html: line }}
        ></span>
      ))}
    </>
  )
}

type GenericErrorAlertProps = {
  title?: string
}

export const GenericErrorAlert: React.FC<GenericErrorAlertProps> = props => {
  const { title } = props
  const { t } = useAppTranslation()

  return (
    <Alert severity={'error'} className={styles.genericErrorAlert} {...injectDataAttributes('errorAlert.genericError')}>
      <AlertHeader>{title ?? t('Common.GenericError.title')}</AlertHeader>
      <AlertContent>
        <Trans
          t={t}
          i18nKey={'Common.GenericError.message'}
          components={{
            phone_anchor: (
              <ButtonLink
                underline
                highlight
                onClick={() => {
                  window.location.href = `tel:${t('ContactInfo.phone')}`
                }}
              />
            ),
          }}
        />
      </AlertContent>
    </Alert>
  )
}
